/**
 * Centralized entity property validation.
 *
 * Strategy:
 * - ReusableValidation.ts: types and helpers used by all entity types.
 * - One file per entity type (e.g. groupValidation.ts, policyRuleValidation.ts) for type-specific rules.
 * - getEntityValidationWarnings() aggregates warnings for view/edit UI (non-blocking).
 * - policyRuleValidation exports validatePolicyRule for save-time validation (used by formValidationUtils).
 */

export {
    getEntityAttr,
    getEntityAttrString,
    getFormValue,
    getDisplayNameFromForm,
    type ValidationWarning,
    type ValidationResult
} from './ReusableValidation';

export { getGroupWarnings } from './groupValidation';
export { getRequestApprovalTypeWarnings, type RequestApprovalTypeValidationOptions } from './requestApprovalTypeValidation';
export { getEmailTemplateWarnings } from './emailTemplateValidation';
export { getSetWarnings } from './setValidation';
export { getFileWarnings } from './fileValidation';
export { getPersonWarnings } from './personValidation';
export { getWorkflowWarnings } from './workflowValidation';
export { getWorkflowActivityWarnings } from './workflowActivityValidation';
export { getApiUserWarnings } from './apiUserValidation';
export { getSetTransitionWarnings } from './setTransitionValidation';
export { validatePolicyRule } from './policyRuleValidation';

import { getPersonWarnings } from './personValidation';
import { getEntityConfig } from '../registry';
import { isPerson } from '../utils/entityTypeDetection';
import type { ValidationWarning } from './ReusableValidation';

/** Optional context for edit-mode validation (e.g. clone mode). */
export interface EntityValidationOptions {
    /** True when creating by cloning an existing entity. */
    isCloneMode?: boolean;
    /** Display name of the entity being cloned (when isCloneMode). */
    existingEntityDisplayName?: string;
}

/**
 * Get validation warnings for an entity (view or edit context).
 * Use these to show Alert/warning UI; they do not block save.
 */
export function getEntityValidationWarnings(
    entityType: string,
    data: Record<string, any>,
    _context: 'view' | 'edit',
    options?: EntityValidationOptions
): ValidationWarning[] {
    if (!entityType || !data) return [];
    const warnings: ValidationWarning[] = [];

    // Person validation applies to all person subtypes (checked via EntitlementsService)
    if (isPerson(entityType)) {
        warnings.push(...getPersonWarnings(data));
    }

    // Registry-driven validation for specific entity types
    const config = getEntityConfig(entityType);
    if (config?.getWarnings) {
        warnings.push(...config.getWarnings(data, options));
    }

    return warnings;
}
